import React from 'react';
import { motion } from 'framer-motion';

const drawTransition = {
  duration: 1.5,
  ease: "easeInOut" as const
};

const IconWrapper: React.FC<{ children: React.ReactNode; label: string; delay: number }> = ({ children, label, delay }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.8 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, amount: 0.5 }}
    transition={{ duration: 0.5, delay }}
    className="flex flex-col items-center gap-4 group"
  >
    <div className="w-24 h-24 md:w-32 md:h-32 bg-zinc-900 border-2 border-zinc-700/50 rounded-[2rem] flex items-center justify-center shadow-[0_4px_0px_0px_rgba(255,255,255,0.1)] group-hover:translate-y-[-4px] group-hover:border-primary group-hover:shadow-[0_8px_20px_rgba(244,63,94,0.2)] transition-all duration-300 relative overflow-hidden">
      {children}
    </div>
    <span className="text-muted font-medium tracking-widest text-xs md:text-sm uppercase">{label}</span>
  </motion.div>
);

const WhatWeDoNew: React.FC = () => {
  return (
    <section className="py-24 px-4 md:px-8 bg-dark text-white">
      <div className="max-w-5xl mx-auto">
        {/* Icon Row */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-16">
          {/* Concept: Gamepad */}
          <IconWrapper label="Concept" delay={0.1}>
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white group-hover:text-primary transition-colors">
              <motion.rect x="8" y="18" width="44" height="28" rx="8" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={drawTransition} />
              <motion.circle cx="20" cy="32" r="6" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{...drawTransition, delay: 0.3}} />
              <motion.circle cx="40" cy="32" r="3" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{...drawTransition, delay: 0.4}} />
              <motion.circle cx="46" cy="26" r="3" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{...drawTransition, delay: 0.5}} />
            </svg>
          </IconWrapper>

          {/* Produce: Video Camera */}
          <IconWrapper label="Produce" delay={0.2}>
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white group-hover:text-primary transition-colors">
              <motion.rect x="8" y="16" width="36" height="28" rx="6" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={drawTransition} />
              <motion.path d="M44 26 L54 20 L54 40 L44 34" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{...drawTransition, delay: 0.3}} />
              <motion.circle cx="26" cy="30" r="6" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{...drawTransition, delay: 0.5}} />
            </svg>
          </IconWrapper>

          {/* Edit: Person/User */}
          <IconWrapper label="Edit" delay={0.3}>
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white group-hover:text-primary transition-colors">
              <motion.circle cx="30" cy="20" r="10" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={drawTransition} />
              <motion.path d="M12 50 C12 38 22 32 30 32 C38 32 48 38 48 50" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{...drawTransition, delay: 0.3}} />
            </svg>
          </IconWrapper>

          {/* Post: Cloud Upload */}
          <IconWrapper label="Post" delay={0.4}>
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white group-hover:text-primary transition-colors">
              <motion.path d="M20 40 C10 40 6 32 10 24 C14 16 26 16 30 22 C34 14 50 14 52 26 C54 38 46 40 40 40" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={drawTransition} />
              <motion.path d="M30 50 L30 32" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{...drawTransition, delay: 0.3}} />
              <motion.path d="M24 38 L30 32 L36 38" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{...drawTransition, delay: 0.4}} />
            </svg>
          </IconWrapper>

          {/* Grow: Chart - Fixed trending line */}
          <IconWrapper label="Grow" delay={0.5}>
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white group-hover:text-primary transition-colors">
              <motion.path d="M10 44 L24 32 L32 38 L50 18" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{...drawTransition, delay: 0.2}} />
              <motion.path d="M40 18 L50 18 L50 28" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{...drawTransition, delay: 0.5}} />
            </svg>
          </IconWrapper>
        </div>

        {/* Text */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center"
        >
          <h2 className="font-bold text-3xl md:text-5xl leading-tight text-white">
            We handle the complete content creation process. <br/> <span className="text-muted">You focus on your game.</span>
          </h2>
        </motion.div>
      </div>
    </section>
  );
};

export default WhatWeDoNew;
